/**
 * StatusChip — chip de estado para encomiendas de Recepción Digital.
 *
 * Decisiones de diseño:
 * - Los estados son la señal de triaje principal del Manifiesto, por eso el color
 *   se centraliza aquí (STATUS_META) y ningún otro componente define colores de estado.
 * - `priority` vive junto al color: el orden "prioridad de operador" del Manifiesto
 *   y del Command Palette se deriva de este mapa, no se duplica.
 * - Colores vía variables CSS del tema (theme.css) para que light/dark funcionen
 *   sin lógica extra. Nada de gradientes ni pills (rounded-md, no rounded-full).
 * - Confianza-como-visual: el chip comunica estado con color + texto, nunca porcentajes.
 * - Estado desconocido: chip neutro con el string crudo — nunca romper el render.
 */

// Claves EXACTAS (con acentos) según el modelo de datos del módulo.
export const STATUS_META = {
  'lista_para_producción': {
    label: 'Lista para producción',
    priority: 0,
    bg: 'var(--green-soft)',
    color: 'var(--green)',
    border: 'var(--green)',
  },
  'necesita_revisión': {
    label: 'Necesita revisión',
    priority: 1,
    bg: 'var(--amber-soft)',
    color: 'var(--amber)',
    border: 'var(--amber)',
  },
  'pendiente_llegada': {
    label: 'Pendiente de llegada',
    priority: 2,
    bg: 'var(--blue-soft)',
    color: 'var(--blue-text)',
    border: 'var(--blue)',
  },
  'esperando_cliente': {
    label: 'Esperando al cliente',
    priority: 3,
    bg: 'var(--bg)',
    color: 'var(--text-secondary)',
    border: 'var(--border-hover)',
  },
  'expirada': {
    label: 'Expirada',
    priority: 4,
    bg: 'var(--red-soft)',
    color: 'var(--avianca-red)',
    border: 'var(--avianca-red)',
  },
};

// Chip secundario: tramo de impuesto del paquete (derivado de taxes.dai > 0).
export const TAX_TIER_META = {
  '30%': {
    label: 'Impuesto 30%',
    bg: 'var(--red-soft)',
    color: 'var(--avianca-red)',
    border: 'var(--avianca-red)',
  },
  'IVA': {
    label: 'IVA',
    bg: 'var(--blue-soft)',
    color: 'var(--blue-text)',
    border: 'var(--blue)',
  },
};

const SIZES = {
  sm: 'text-[11px] px-2 py-0.5',
  md: 'text-xs px-2.5 py-1',
};

const StatusChip = ({ status, size = 'sm' }) => {
  const meta = STATUS_META[status];
  const label = meta ? meta.label : String(status || '—');
  const style = meta
    ? { background: meta.bg, color: meta.color, borderColor: meta.border }
    : {
        background: 'var(--bg)',
        color: 'var(--text-secondary)',
        borderColor: 'var(--border)',
      };

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md border font-medium whitespace-nowrap ${SIZES[size] || SIZES.sm}`}
      style={style}
      aria-label={`Estado: ${label}`}
    >
      {label}
    </span>
  );
};

export default StatusChip;

/*
Ejemplo de uso:
  <StatusChip status="lista_para_producción" />
  <StatusChip status="esperando_cliente" size="md" />

Chip de impuesto (mismo patrón visual, meta distinta):
  const tier = TAX_TIER_META['30%'];
  <span style={{ background: tier.bg, color: tier.color, borderColor: tier.border }}
        className="inline-flex items-center rounded-md border text-[11px] px-2 py-0.5 font-medium">
    {tier.label}
  </span>

Orden de triaje:
  drafts.sort((a, b) =>
    (STATUS_META[a.status]?.priority ?? 99) - (STATUS_META[b.status]?.priority ?? 99));
*/
